import React from 'react';
import { VisualAid } from '../../types';
import { MetricStairsCompact } from './MetricStairsCompact';
import { FractionPizza } from './FractionPizza';
import { FractionVisual } from './FractionVisual';
import { GeoGrid } from './GeoGrid';
import { NumberLine } from './NumberLine';
import { RatioTable } from './RatioTable';
import { RuleCard } from './RuleCard';
import { MoneyVisual } from './MoneyVisual';
import { ColumnVisual } from './ColumnVisual';
interface VisualAidFactoryProps {
  visualAid: VisualAid;
  isCorrect?: boolean;
}
/**
 * Helper to determine if a visual aid should be shown based on correctness state
 */
export function shouldShowVisualAid(visualAid?: VisualAid, isCorrect?: boolean): boolean {
  if (!visualAid || !visualAid.type || !visualAid.data) return false;
  const {
    showWhen
  } = visualAid;
  // 'always' is default if not specified
  if (!showWhen || showWhen === 'always') return true;
  if (showWhen === 'correct' && isCorrect) return true;
  if (showWhen === 'wrong' && !isCorrect) return true;
  return false;
}
export function VisualAidFactory({
  visualAid,
  isCorrect
}: VisualAidFactoryProps) {
  // Use the helper to decide visibility
  if (!shouldShowVisualAid(visualAid, isCorrect)) return null;
  const {
    type,
    data
  } = visualAid;
  try {
    switch (type) {
      case 'stairs':
        if (!data.type) return null;
        return <MetricStairsCompact type={data.type as any} from={data.from as any} to={data.to as any} value={data.value as number} highlight={data.highlight as boolean} showAnimation={data.showAnimation as boolean} data-id="element-1879" />;
      case 'fraction':
        {
          const numerator = data.numerator as number || Math.round((data.value as number || 0) * (data.denominator as number || 4));
          const denominator = data.denominator as number || 4;
          if (denominator === 0) return null;
          return <FractionPizza numerator={numerator} denominator={denominator} label={data.label as string} type={data.visualType as any || 'pizza'} showEaten={data.showEaten as boolean} usePizza={data.usePizza !== false} data-id="element-1880" />;
        }
      case 'geo':
        if (!data.width || !data.height) return null;
        return <GeoGrid {...data as any} data-id="element-1881" />;
      case 'numberline':
        return <NumberLine {...data as any} data-id="element-1882" />;
      case 'ratio':
        if (!data.headers || !data.rows) return null;
        return <RatioTable {...data as any} data-id="element-1883" />;
      case 'rule':
        if (!data.title || !data.content) return null;
        return <RuleCard {...data as any} data-id="element-1884" />;
      case 'money':
        if (data.amount === undefined || data.amount === null) return null;
        return <MoneyVisual amount={data.amount as number} breakdown={data.breakdown as any} showCoins={data.showCoins !== false} showBills={data.showBills !== false} interactive={data.interactive as boolean} label={data.label as string} data-id="element-1885" />;
      case 'column':
        if (!data.operation) return null;
        const operand1 = data.operand1 ?? (data.numbers as any)?.[0];
        const operand2 = data.operand2 ?? (data.numbers as any)?.[1];
        if (operand1 === undefined || operand2 === undefined) return null;
        return <ColumnVisual operation={data.operation as any} operand1={operand1} operand2={operand2} showSteps={data.showSteps !== false} currentStep={data.currentStep as number} animated={data.animated !== false} onStepChange={data.onStepChange as any} data-id="element-1886" />;
      default:
        console.warn(`Unknown visual aid type: ${type}`);
        return null;
    }
  } catch (error) {
    console.warn('Error rendering visual aid:', error);
    return null;
  }
}